import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import type { WhellDrumColumnProps } from './types'

const ITEM_HEIGHT = 44
const VISIBLE_ITEMS = 5
const DRUM_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS
const SIDE_PADDING = ITEM_HEIGHT * 2

function WhellDrumColumn<T extends { label: string }>({
  items,
  selectedIndex,
  onSelect,
  label,
}: WhellDrumColumnProps<T>) {
  const listRef = useRef<HTMLUListElement>(null)

  const isDragging = useRef(false)
  const dragStartY = useRef(0)
  const dragStartScroll = useRef(0)

  const animationFrame = useRef<number | null>(null)
  const settleTimeout = useRef<number | null>(null)
  const settling = useRef(false)

  const scrollForIndex = useCallback((index: number) => {
    return index * ITEM_HEIGHT
  }, [])

  const indexFromScroll = useCallback(
    (scrollTop: number) => {
      if (items.length === 0) return 0

      return Math.max(0, Math.min(items.length - 1, Math.round(scrollTop / ITEM_HEIGHT)))
    },
    [items.length],
  )

  const cancelAnimation = useCallback(() => {
    if (animationFrame.current !== null) {
      cancelAnimationFrame(animationFrame.current)
      animationFrame.current = null
    }
  }, [])

  const cancelSettleTimeout = useCallback(() => {
    if (settleTimeout.current !== null) {
      window.clearTimeout(settleTimeout.current)
      settleTimeout.current = null
    }
  }, [])

  const snapTo = useCallback(
    (index: number, onDone?: () => void) => {
      const element = listRef.current

      if (!element || items.length === 0) {
        return
      }

      cancelAnimation()

      const safeIndex = Math.max(0, Math.min(items.length - 1, index))

      const start = element.scrollTop
      const target = scrollForIndex(safeIndex)
      const distance = target - start

      if (Math.abs(distance) < 1) {
        element.scrollTop = target
        onDone?.()
        return
      }

      settling.current = true

      const duration = 220
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const progress = Math.min((currentTime - startTime) / duration, 1)

        const ease = 1 - Math.pow(1 - progress, 3)

        element.scrollTop = start + distance * ease

        if (progress < 1) {
          animationFrame.current = requestAnimationFrame(animate)
        } else {
          element.scrollTop = target

          animationFrame.current = null
          settling.current = false

          onDone?.()
        }
      }

      animationFrame.current = requestAnimationFrame(animate)
    },
    [cancelAnimation, items.length, scrollForIndex],
  )

  const commitIndex = useCallback(
    (index: number) => {
      if (items.length === 0) return

      const safeIndex = Math.max(0, Math.min(items.length - 1, index))

      snapTo(safeIndex, () => {
        onSelect(safeIndex)
      })
    },
    [items.length, onSelect, snapTo],
  )

  /*
   * Initial position
   */
  useLayoutEffect(() => {
    const element = listRef.current

    if (!element) return

    element.scrollTop = scrollForIndex(selectedIndex)
  }, [scrollForIndex, selectedIndex])

  /*
   * External selected index change
   */
  const previousSelectedIndex = useRef(selectedIndex)

  useEffect(() => {
    if (previousSelectedIndex.current === selectedIndex) {
      return
    }

    previousSelectedIndex.current = selectedIndex

    if (settling.current) return

    snapTo(selectedIndex)
  }, [selectedIndex, snapTo])

  /*
   * Native scrolling
   */
  const handleScroll = useCallback(() => {
    if (isDragging.current || settling.current) {
      return
    }

    cancelSettleTimeout()

    settleTimeout.current = window.setTimeout(() => {
      const element = listRef.current

      if (!element) return

      const index = indexFromScroll(element.scrollTop)

      commitIndex(index)
    }, 120)
  }, [cancelSettleTimeout, commitIndex, indexFromScroll])

  /*
   * Mouse wheel
   */
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault()

      if (settling.current) return

      const element = listRef.current

      if (!element) return

      const currentIndex = indexFromScroll(element.scrollTop)

      const direction = event.deltaY > 0 ? 1 : -1

      commitIndex(currentIndex + direction)
    },
    [commitIndex, indexFromScroll],
  )

  /*
   * Mouse dragging
   */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      cancelAnimation()
      cancelSettleTimeout()

      isDragging.current = true

      dragStartY.current = event.clientY

      dragStartScroll.current = listRef.current?.scrollTop ?? 0

      event.preventDefault()
    },
    [cancelAnimation, cancelSettleTimeout],
  )

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging.current) return

      const element = listRef.current

      if (!element) return

      const delta = dragStartY.current - event.clientY

      element.scrollTop = dragStartScroll.current + delta
    }

    const handleMouseUp = () => {
      if (!isDragging.current) return

      isDragging.current = false

      const element = listRef.current

      if (!element) return

      const index = indexFromScroll(element.scrollTop)

      commitIndex(index)
    }

    window.addEventListener('mousemove', handleMouseMove)

    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)

      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [commitIndex, indexFromScroll])

  /*
   * Wheel listener
   */
  useEffect(() => {
    const element = listRef.current

    if (!element) return

    element.addEventListener('wheel', handleWheel, {
      passive: false,
    })

    return () => {
      element.removeEventListener('wheel', handleWheel)
    }
  }, [handleWheel])

  /*
   * Cleanup
   */
  useEffect(() => {
    return () => {
      cancelAnimation()
      cancelSettleTimeout()
    }
  }, [cancelAnimation, cancelSettleTimeout])

  /*
   * Click
   */
  const handleItemClick = useCallback(
    (index: number) => {
      if (isDragging.current) return

      commitIndex(index)
    },
    [commitIndex],
  )

  return (
    <div
      style={{
        width: 76,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        userSelect: 'none',
      }}
    >
      {/* Label */}
      <div
        style={{
          fontSize: 14,
          color: '#777',
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {label}
      </div>

      {/* Drum */}
      <div
        style={{
          position: 'relative',
          width: 68,
          height: DRUM_HEIGHT,
          overflow: 'hidden',
          borderRadius: 16,
          flexShrink: 0,
        }}
      >
        {/* Top fade */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: SIDE_PADDING,
            zIndex: 20,
            pointerEvents: 'none',
            background: 'linear-gradient(to bottom, #fff 10%, transparent 100%)',
          }}
        />

        {/* Bottom fade */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: SIDE_PADDING,
            zIndex: 20,
            pointerEvents: 'none',
            background: 'linear-gradient(to top, #fff 10%, transparent 100%)',
          }}
        />

        {/* IMPORTANT: actual scroll container */}
        <ul
          ref={listRef}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
            overflowY: 'scroll',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            listStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            overscrollBehavior: 'contain',
            cursor: 'grab',
          }}
        >
          {/* Top spacer */}
          <li
            style={{
              height: SIDE_PADDING,
              flexShrink: 0,
            }}
          />

          {items.map((item, index) => {
            const distance = Math.abs(index - selectedIndex)

            const isSelected = index === selectedIndex

            return (
              <li
                key={`${item.label}-${index}`}
                onClick={() => handleItemClick(index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: ITEM_HEIGHT,
                  minHeight: ITEM_HEIGHT,
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    fontWeight: 700,
                    fontSize: isSelected ? 17 : 15,
                    lineHeight: 1,
                    color: isSelected ? '#111' : '#777',
                    opacity: distance > 1 ? 0.4 : isSelected ? 1 : 0.7,
                    transform: isSelected ? 'scale(1.08)' : 'scale(0.96)',
                    transition: 'all 150ms ease',
                  }}
                >
                  {item.label}
                </span>
              </li>
            )
          })}

          {/* Bottom spacer */}
          <li
            style={{
              height: SIDE_PADDING,
              flexShrink: 0,
            }}
          />
        </ul>
      </div>
    </div>
  )
}

export { WhellDrumColumn }
